import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Plus, Trash2, X, Loader2, Video, ListChecks } from 'lucide-react';
import { listMeetings, createMeeting, deleteMeeting } from '../lib/marketingApi';

const PROVIDERS = [
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'phone', label: 'Phone' },
  { value: 'other', label: 'Other' },
];
const EMPTY = { title: '', scheduled_at: '', provider: 'teams', meeting_url: '', summary: '', attendees: [{ name: '', role: '', email: '' }], next_steps: [''] };

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

export default function ContactMeetings({ contactId, canEdit, onLogged }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => { listMeetings(contactId).then((d) => setItems(d.items || [])).catch(() => setItems([])); }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const setAtt = (i, k, v) => setForm((f) => ({ ...f, attendees: f.attendees.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));
  const setStep = (i, v) => setForm((f) => ({ ...f, next_steps: f.next_steps.map((s, idx) => idx === i ? v : s) }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Add a meeting title'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        attendees: form.attendees.filter((a) => a.name.trim() || a.email.trim()),
        next_steps: form.next_steps.map((s) => s.trim()).filter(Boolean),
      };
      await createMeeting(contactId, payload);
      toast.success('Meeting logged');
      setForm(EMPTY); setOpen(false); load(); onLogged && onLogged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to log meeting'); }
    finally { setSaving(false); }
  };

  const remove = async (mid) => {
    try { await deleteMeeting(mid); toast.success('Meeting removed'); load(); }
    catch (e) { toast.error('Failed to remove'); }
  };

  const inputCls = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0035A8]';

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="contact-meetings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarClock className="h-4 w-4 text-[#0035A8]" /> Meetings ({(items || []).length})</div>
        {canEdit && (
          <button onClick={() => setOpen((v) => !v)} data-testid="meeting-add-toggle"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#0035A8] px-3 py-1.5 text-xs font-semibold text-[#0035A8] hover:bg-[#0035A8] hover:text-white">
            {open ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {open ? 'Cancel' : 'Log meeting'}
          </button>
        )}
      </div>

      {open && canEdit && (
        <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="meeting-form">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Meeting title (e.g. Discovery call)" data-testid="meeting-title" className={inputCls} />
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="meeting-datetime" className={inputCls} />
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className={inputCls} data-testid="meeting-provider">
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <input value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} placeholder="Meeting link (optional)" className={inputCls} />
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Attendees (colleagues + roles)</div>
            {form.attendees.map((a, i) => (
              <div key={i} className="mb-1.5 grid grid-cols-12 gap-1.5">
                <input value={a.name} onChange={(e) => setAtt(i, 'name', e.target.value)} placeholder="Name" data-testid={`meeting-attendee-name-${i}`} className={`col-span-4 ${inputCls}`} />
                <input value={a.role} onChange={(e) => setAtt(i, 'role', e.target.value)} placeholder="Role" data-testid={`meeting-attendee-role-${i}`} className={`col-span-3 ${inputCls}`} />
                <input value={a.email} onChange={(e) => setAtt(i, 'email', e.target.value)} placeholder="Email" className={`col-span-4 ${inputCls}`} />
                <button onClick={() => setForm((f) => ({ ...f, attendees: f.attendees.filter((_, idx) => idx !== i) }))} className="col-span-1 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => setForm((f) => ({ ...f, attendees: [...f.attendees, { name: '', role: '', email: '' }] }))} data-testid="meeting-add-attendee" className="text-xs font-semibold text-[#0035A8] hover:underline">+ Add attendee</button>
          </div>

          <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} placeholder="Summary — what was discussed, the company and their problem…" data-testid="meeting-summary" className={inputCls} />

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Next steps</div>
            {form.next_steps.map((s, i) => (
              <div key={i} className="mb-1.5 flex gap-1.5">
                <input value={s} onChange={(e) => setStep(i, e.target.value)} placeholder="Next step" data-testid={`meeting-next-step-${i}`} className={inputCls} />
                <button onClick={() => setForm((f) => ({ ...f, next_steps: f.next_steps.filter((_, idx) => idx !== i) }))} className="flex items-center rounded-md px-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => setForm((f) => ({ ...f, next_steps: [...f.next_steps, ''] }))} data-testid="meeting-add-step" className="text-xs font-semibold text-[#0035A8] hover:underline">+ Add next step</button>
          </div>

          <div className="flex justify-end">
            <button onClick={submit} disabled={saving} data-testid="meeting-save" className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save meeting
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {items === null ? (
          <div className="flex justify-center py-6 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">No meetings logged yet.</p>
        ) : items.map((m) => (
          <div key={m.id} className="rounded-lg border border-slate-200 p-4" data-testid={`meeting-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">{m.title || 'Call'}</div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                  {m.scheduled_at && <span>{fmt(m.scheduled_at)}</span>}
                  {m.provider && <span className="inline-flex items-center gap-1"><Video className="h-3 w-3" /> {m.provider}</span>}
                </div>
              </div>
              {canEdit && <button onClick={() => remove(m.id)} data-testid={`meeting-delete-${m.id}`} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
            {(m.attendees || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {m.attendees.map((a, i) => (
                  <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{a.name}{a.role ? ` · ${a.role}` : ''}</span>
                ))}
              </div>
            )}
            {m.summary && <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{m.summary}</p>}
            {(m.next_steps || []).length > 0 && (
              <div className="mt-2">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><ListChecks className="h-3.5 w-3.5" /> Next steps</div>
                <ul className="space-y-1">
                  {m.next_steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0035A8]" /><span>{s}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
